import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { BASE_URL } from "../API/Constants";
import { guestCart } from "../components/Products";

const safe = (v, fb = "-") => (v === null || v === undefined || v === "" ? fb : v);
const money = (v) => `₹${Number(v || 0).toFixed(0)}`;

export default function SubcategoriesProductsSplitWhite() {
  const nav = useNavigate();
  const { categoryId } = useParams();
  const location = useLocation();
  const categoryFromState = location?.state?.category || null;

  const [subcats, setSubcats] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeSub, setActiveSub] = useState(null);

  const [loadingSub, setLoadingSub] = useState(true);
  const [loadingProd, setLoadingProd] = useState(false);
  const [err, setErr] = useState("");

  const [cartMap, setCartMap] = useState({});
  const [busyKey, setBusyKey] = useState("");

  // ✅ IMPORTANT: IMAGE ORIGIN MUST NOT HAVE /api
  const IMAGE_ORIGIN = useMemo(() => {
    const b = (BASE_URL || "").trim().replace(/\/+$/, "");
    return b.replace(/\/api$/i, "");
  }, []);

  const imgUrl = (path) => {
    if (!path) return "";
    const p = String(path).trim();
    if (!p) return "";
    if (p.startsWith("http://") || p.startsWith("https://")) return p;
    if (p.startsWith("/")) return `${IMAGE_ORIGIN}${p}`;
    return `${IMAGE_ORIGIN}/${p}`;
  };

  const pickList = (data, keys = []) => {
    if (Array.isArray(data)) return data;
    for (const k of keys) if (Array.isArray(data?.[k])) return data[k];
    return [];
  };

  const parseImages = (imagesField) => {
    if (!imagesField) return [];
    if (Array.isArray(imagesField)) return imagesField;

    const t = String(imagesField).trim();
    if (!t) return [];

    try {
      const parsed = JSON.parse(t);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === "string") return [parsed];
      return [];
    } catch {
      return [t];
    }
  };

  // ✅ LOAD CART
  const loadCart = async () => {
    try {
      const res = await axiosInstance.get("/api/cart");
      const d = res.data || {};
      const items = (d.CartItems || d.cartItems || d.items || []) ?? [];

      const next = {};
      for (const it of items) {
        const prod = it?.product || it?.Product || it?.productDetails || {};
        const pid = prod?.id ?? it?.productId ?? it?.ProductId ?? it?.product_id;
        const cartItemId = it?.id ?? it?.cartItemId ?? it?._id;
        const qty = Number(it?.qty ?? it?.quantity ?? it?.cartQty ?? 1);

        if (pid != null) next[String(pid)] = { cartItemId, qty };
      }
      setCartMap(next);
      // ✅ Also broadcast to Navbar
      const total = Object.values(next).reduce((s, r) => s + (Number(r?.qty) || 0), 0);
      window.dispatchEvent(new CustomEvent("cartCountUpdated", { detail: { count: total } }));
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      loadCart();
    } else {
      // load guest cart
      const raw = guestCart.get();
      setCartMap(guestCart.toCartMap(raw));
      window.dispatchEvent(new CustomEvent("cartCountUpdated", { detail: { count: guestCart.totalCount(raw) } }));
    }
  }, []);

  // ✅ Load Subcategories (only)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingSub(true);
        setErr("");
        setSubcats([]);
        setProducts([]);
        setActiveSub(null);

        const res = await axiosInstance.get("/api/subcategories", { params: { categoryId } });
        const list = pickList(res.data, ["subcategories", "subCategories", "items", "data"]);

        if (!mounted) return;
        setSubcats(list || []);

        // ✅ AUTO SELECT FIRST SUB
        if (list && list.length > 0) {
          onSelectSub(list[0]);
        }
      } catch (e) {
        if (!mounted) return;
        setErr(e?.response?.data?.msg || e?.response?.data?.message || e?.message || "Failed to load subcategories");
      } finally {
        if (mounted) setLoadingSub(false);
      }
    })();

    return () => (mounted = false);
  }, [categoryId]);

  // ✅ Load Products on Subcategory click
  const onSelectSub = async (s) => {
    setActiveSub(s);
    setProducts([]);

    try {
      setLoadingProd(true);
      setErr("");

      const res = await axiosInstance.get("/api/products", { params: { subCategoryId: s?.id } });
      const list = pickList(res.data, ["products", "data", "items"]);

      setProducts(list || []);
    } catch (e) {
      setProducts([]);
      setErr(e?.response?.data?.msg || e?.response?.data?.message || e?.message || "Failed to load products");
    } finally {
      setLoadingProd(false);
    }
  };

  const addToCart = async (e, p) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!p?.id) return;

    const token = localStorage.getItem("token");
    if (!token) {
      const updated = guestCart.add(p.id, p);
      const map = guestCart.toCartMap(updated);
      setCartMap(map);
      window.dispatchEvent(new CustomEvent("cartCountUpdated", { detail: { count: guestCart.totalCount(updated) } }));
      return;
    }

    try {
      setBusyKey(`add:${p.id}`);
      await axiosInstance.post("/api/cart", { productId: p.id });
      await loadCart();
    } catch (er) {
      console.error(er);
    } finally {
      setBusyKey("");
    }
  };

  const updateCartQty = async (e, p, newQty) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!p?.id) return;

    const pid = String(p.id);
    const row = cartMap[pid];

    const token = localStorage.getItem("token");
    if (!token) {
      const updated = guestCart.updateQty(p.id, newQty);
      const map = guestCart.toCartMap(updated);
      setCartMap(map);
      window.dispatchEvent(new CustomEvent("cartCountUpdated", { detail: { count: guestCart.totalCount(updated) } }));
      return;
    }

    if (!row?.cartItemId) {
      if (newQty >= 1) return addToCart(e, p);
      return;
    }
    if (newQty < 1) {
      try {
        setBusyKey(`qty:${p.id}`);
        await axiosInstance.delete(`/api/cart/${row.cartItemId}`);
        await loadCart();
      } catch (er) { console.error(er); }
      finally { setBusyKey(""); }
      return;
    }

    try {
      setBusyKey(`qty:${p.id}`);
      await axiosInstance.put(`/api/cart/${row.cartItemId}`, { qty: newQty });

      setCartMap((prev) => ({
        ...prev,
        [pid]: { ...prev[pid], qty: newQty },
      }));
      // update count in navbar
      loadCart();
    } catch (er) {
      console.error(er);
      await loadCart();
    } finally {
      setBusyKey("");
    }
  };

  const renderStars = () => {
    return (
      <div className="spw-stars">
        {[1, 2, 3, 4].map(i => (
          <svg key={i} className="star-filled" viewBox="0 0 24 24" width="14" height="14">
            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
          </svg>
        ))}
        <svg className="star-empty" viewBox="0 0 24 24" width="14" height="14">
          <path d="M22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z" />
        </svg>
        <span className="spw-rev-count">(128)</span>
      </div>
    );
  };

  return (
    <div className="spw-page">
      <style>{css}</style>

      <div className="spw-container">
        {/* Top bar */}
        <div className="spw-top">
          <button className="spw-back" onClick={() => nav(-1)} type="button">
            ← Back
          </button>

          <div className="spw-headText">
            <div className="spw-title">Subcategories</div>
            <div className="spw-sub">
              Category: <b>{safe(categoryFromState?.name, `#${categoryId}`)}</b>
            </div>
          </div>
        </div>

        {err ? <div className="spw-msg spw-err">{err}</div> : null}

        {/* MAIN SPLIT */}
        <div className="spw-split">
          {/* LEFT: Subcategories */}
          <aside className="spw-left">
            <div className="spw-leftHead">
              <div className="spw-leftTitle">All Subcategories</div>
              <div className="spw-leftSub">{loadingSub ? "Loading..." : `${subcats.length} items`}</div>
            </div>

            {loadingSub ? (
              <div className="spw-subList">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div className="spw-subSkel" key={i} />
                ))}
              </div>
            ) : subcats.length === 0 ? (
              <div className="spw-empty">No subcategories found</div>
            ) : (
              <div className="spw-subList">
                {subcats.map((s) => {
                  const active = String(activeSub?.id) === String(s?.id);
                  return (
                    <button
                      key={s?.id}
                      type="button"
                      className={"spw-subItem" + (active ? " active" : "")}
                      onClick={() => onSelectSub(s)}
                      title={s?.name}
                    >
                      <div className="spw-subThumb">
                        <img
                          src={imgUrl(s?.image) || "https://via.placeholder.com/80?text=S"}
                          alt={s?.name || "Subcategory"}
                          onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/80?text=S")}
                        />
                      </div>
                      <div className="spw-subName">{safe(s?.name)}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </aside>

          {/* RIGHT: Products */}
          <main className="spw-right">
            <div className="spw-rightHead">
              <div>
                <div className="spw-rightTitle">Products</div>
                <div className="spw-sub">
                  Subcategory: <b>{activeSub ? safe(activeSub?.name) : "Select a subcategory"}</b>
                </div>
              </div>

              <div className="spw-pill">
                {loadingProd ? "Loading..." : activeSub ? `${products.length} items` : "—"}
              </div>
            </div>

            {!activeSub ? (
              <div className="spw-msg">
                Click any subcategory on the left to show products.
              </div>
            ) : loadingProd ? (
              <div className="spw-prodGrid">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div className="spw-prodSkel" key={i} />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="spw-msg">No products found</div>
            ) : (
              <div className="spw-prodGrid">
                {products.map((p) => {
                  const imgs = parseImages(p?.images);
                  const first = imgs?.[0] ? imgUrl(imgs[0]) : imgUrl(p?.image);
                  
                  const pidStr = String(p?.id ?? "");
                  const q = Number(cartMap?.[pidStr]?.qty || 0);
                  const isBusy = busyKey === `add:${p?.id}` || busyKey === `qty:${p?.id}`;
                  const inStock = Number(p?.stockQty || 0) > 0;

                  return (
                    <div
                      key={p?.id}
                      className="spw-prodCard"
                      onClick={() => nav(`/productdetails/${p?.id}`)}
                      title={p?.name}
                    >
                      <div className="spw-prodImg">
                        <img
                          src={first || "https://via.placeholder.com/500x400?text=P"}
                          alt={p?.name || "Product"}
                          onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/500x400?text=P")}
                        />
                      </div>

                      <div className="spw-prodBody">
                        <div className="spw-prodName">{safe(p?.name)}</div>
                        
                        {renderStars()}

                        <div className="spw-prodMeta">
                          <span className="spw-brand">{safe(p?.brand)}</span>
                          <span className="spw-price">{money(p?.price)}</span>
                        </div>

                        <div className="spw-small">
                          {safe(p?.packSize, "") ? `${safe(p?.packSize)} pcs` : ""}{" "}
                          {safe(p?.manufacturer, "") ? `• ${safe(p?.manufacturer)}` : ""}
                        </div>

                        <div className="spw-card-actions">
                          {!inStock ? (
                            <button className="spw-add-btn" disabled>Out of stock</button>
                          ) : q <= 0 ? (
                            <button className="spw-add-btn" type="button" onClick={(e) => addToCart(e, p)} disabled={isBusy}>
                              {isBusy ? "..." : "ADD"}
                            </button>
                          ) : (
                            <div className="spw-qty-box" onClick={(e) => e.stopPropagation()}>
                              <button type="button" className="spw-qty-btn" disabled={isBusy} onClick={(e) => updateCartQty(e, p, q - 1)}>−</button>
                              <div className="spw-qty-val">{q}</div>
                              <button type="button" className="spw-qty-btn" disabled={isBusy} onClick={(e) => updateCartQty(e, p, q + 1)}>+</button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&display=swap');
  *{ box-sizing:border-box; }
  body{ margin:0; background:#fff; font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto; color:#0b1220; }
  .spw-page{ min-height:100vh; padding:16px 0 28px; background:#fff; }
  .spw-container{ width:min(1400px, 100%); margin:0 auto; padding:0 16px; }

  :root{
    --g: rgba(30,204,176,.22);
    --g2: rgba(30,204,176,.08);
    --text: #1a1a1a;
    --muted: #666;
    --bgSoft: #f0fdfd;
    --shadow: 0 10px 40px rgba(0,0,0,.04);
    --r: 16px;
  }

  .spw-top{ display:flex; align-items:center; gap:20px; margin-bottom:24px; padding: 10px 0; }
  .spw-back{
    border: 1px solid rgba(0,0,0,0.06);
    background:#fff;
    padding:10px 16px;
    border-radius:12px;
    cursor:pointer;
    font-weight:700;
    color:#1a1a1a;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    transition: all 0.2s ease;
  }
  .spw-back:hover { transform: translateX(-4px); border-color: #1ECCB0; color: #1ECCB0; }

  .spw-title{ font-size:24px; font-weight:800; color:var(--text); font-family: 'Playfair Display', serif; }
  .spw-sub{ margin-top:8px; font-size:14px; font-weight:500; color:var(--muted); }

  .spw-msg{
    margin:20px 0;
    padding:16px 20px;
    border-radius:16px;
    background:#fff;
    border:1px solid var(--g2);
    color: #444;
    font-weight:500;
    box-shadow: var(--shadow);
    text-align: center;
  }
  .spw-err{ border-color: rgba(239,68,68,.25); background: rgba(239,68,68,.06); color:#7f1d1d; }

  .spw-split{
    display:grid;
    grid-template-columns: 280px 1fr;
    gap: 24px;
    align-items: start;
  }
  @media (max-width: 900px){
    .spw-split{ grid-template-columns: 1fr; gap: 20px; }
  }

  .spw-left{
    border:1px solid rgba(0,0,0,0.05);
    border-radius: 20px;
    background:#fff;
    box-shadow: var(--shadow);
    overflow:hidden;
    position: sticky;
    top: 80px;
    max-height: calc(100vh - 120px);
  }
  @media (max-width: 900px){
    .spw-left{
      position: relative;
      max-height: none;
      top: 0;
    }
  }
  .spw-leftHead{
    padding: 18px;
    border-bottom: 1px solid rgba(0,0,0,.04);
    background: linear-gradient(180deg, #ffffff, #f9fdfd);
  }
  .spw-leftTitle{ font-size: 16px; font-weight: 800; color: var(--text); }
  .spw-leftSub{ margin-top:6px; font-size:13px; font-weight: 500; color: var(--muted); }

  .spw-subList{
    padding: 12px;
    display:flex;
    flex-direction: column;
    gap: 12px;
    overflow-y: auto;
  }
  @media (max-width: 900px){
    .spw-subList{
      flex-direction: row;
      overflow-x: auto;
      overflow-y: hidden;
      padding: 12px;
    }
    .spw-subList::-webkit-scrollbar{ height: 4px; }
  }

  .spw-subItem{
    border:1px solid rgba(0,0,0,0.03);
    background:#fcfcfc;
    border-radius: 14px;
    padding: 12px;
    cursor:pointer;
    display:flex;
    align-items: center;
    gap: 14px;
    text-align:left;
    width: 100%;
    transition: all 0.2s ease;
  }
  @media (max-width: 900px) { .spw-subItem { min-width: 180px; } }
  
  .spw-subItem:hover{ background: #fff; transform: scale(1.02); box-shadow: 0 4px 15px rgba(0,0,0,0.04); }
  .spw-subItem.active{
    border-color: #1ECCB0;
    background: #f0fdfb;
    box-shadow: 0 4px 20px rgba(30,204,176,0.08);
  }

  .spw-subThumb{
    width:48px; height:48px;
    border-radius: 10px;
    overflow:hidden;
    background: #fff;
    border:1px solid rgba(0,0,0,0.05);
    flex-shrink: 0;
  }
  .spw-subThumb img{ width:100%; height:100%; object-fit:contain; display:block; padding: 4px; }
  .spw-subName{
    font-weight: 700;
    font-size: 14px;
    color: var(--text);
    white-space: nowrap;
    overflow:hidden;
    text-overflow: ellipsis;
  }

  .spw-subSkel{
    height: 72px;
    border-radius: 14px;
    background: #f1f1f1;
    animation: shimmer 1.2s infinite;
  }
  @keyframes shimmer{ 0%{opacity: 0.5;} 50%{opacity: 1;} 100%{opacity: 0.5;} }

  .spw-empty{ padding: 20px; color: var(--muted); font-weight: 500; font-size: 14px; text-align: center; }

  .spw-right{
    border:1px solid rgba(0,0,0,0.04);
    border-radius: 20px;
    background:#fff;
    box-shadow: var(--shadow);
    overflow:hidden;
  }
  .spw-rightHead{
    padding: 18px 24px;
    border-bottom: 1px solid rgba(0,0,0,.04);
    background: linear-gradient(180deg, #ffffff, #f9fdfd);
    display:flex;
    align-items:center;
    justify-content: space-between;
    gap: 20px;
    flex-wrap: wrap;
  }
  .spw-rightTitle{ font-size: 20px; font-weight: 800; color: var(--text); }

  .spw-pill{
    padding: 10px 18px;
    border-radius: 999px;
    border: 1px solid #1ECCB0;
    background: #f0fdfb;
    font-weight: 700;
    font-size: 13px;
    color: #1ECCB0;
  }

  .spw-prodGrid{
    padding: 24px;
    display:grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }
  @media (max-width: 1200px){ .spw-prodGrid{ grid-template-columns: repeat(3, 1fr); } }
  @media (max-width: 640px){ .spw-prodGrid{ grid-template-columns: repeat(2, 1fr); gap: 12px; padding: 12px; } }

  .spw-prodCard{
    border:1px solid rgba(0,0,0,0.04);
    background:#fff;
    border-radius: 18px;
    overflow:hidden;
    cursor:pointer;
    padding:0;
    text-align:left;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(0,0,0,0.03);
    display: flex;
    flex-direction: column;
  }
  .spw-prodCard:hover{ transform: translateY(-4px); box-shadow: 0 12px 30px rgba(0,0,0,0.08); border-color: #1ECCB0; }

  .spw-prodImg{
    aspect-ratio: 1 / 1;
    background: #f8f8f8;
    overflow: hidden;
    padding: 12px;
  }
  .spw-prodImg img{ width:100%; height:100%; object-fit: contain; display:block; transition: .3s ease; }
  .spw-prodCard:hover .spw-prodImg img { transform: scale(1.08); }

  .spw-prodBody{ padding: 14px; flex: 1; display: flex; flex-direction: column; }
  .spw-prodName{
    font-weight: 700;
    font-size: 14px;
    color: var(--text);
    line-height: 1.3;
    min-height: 36px;
    display:-webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow:hidden;
  }

  .spw-stars{ display: flex; align-items: center; gap: 2px; margin-top: 6px; }
  .star-filled{ fill: #f39c12; }
  .star-empty{ fill: #ccc; }
  .spw-rev-count{ font-size: 11px; color: #999; margin-left: 4px; font-weight: 600; }

  .spw-prodMeta{
    margin-top: 8px;
    display:flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
  }
  .spw-price{ color: #1ECCB0; font-weight: 800; font-size: 15px; }
  .spw-brand{ color: var(--muted); font-size: 12px; font-weight: 600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

  .spw-small{
    margin-top: 6px;
    font-size: 11px;
    font-weight: 600;
    color: #999;
    white-space: nowrap;
    overflow:hidden;
    text-overflow: ellipsis;
  }

  .spw-card-actions{ margin-top: auto; padding-top: 14px; }
  .spw-add-btn{
    width: 100%;
    background: #006600;
    color: #fff;
    border: none;
    padding: 10px;
    border-radius: 10px;
    font-weight: 700;
    cursor: pointer;
    transition: .2s;
  }
  .spw-add-btn:hover:not(:disabled){ background: #004d00; }
  .spw-add-btn:disabled{ background: #eee; color: #999; cursor: not-allowed; }

  .spw-qty-box{
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f0fdf2;
    border: 1px solid #006600;
    border-radius: 10px;
    overflow: hidden;
  }
  .spw-qty-btn{
    width: 34px;
    height: 38px;
    border: none;
    background: transparent;
    color: #1ECCB0;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
  }
  .spw-qty-btn:hover{ background: rgba(0,102,0,0.1); }
  .spw-qty-val{ font-weight: 700; color: #006600; font-size: 15px; }

  .spw-prodSkel{
    aspect-ratio: 1 / 1.4;
    border-radius: 18px;
    background: #f1f1f1;
    animation: shimmer 1.2s infinite;
  }
`;